import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../Components/Common/Sidebar/Sidebar";
import CollectionDescription from "../../Components/Features/CollectionDescription/CollectionDescription";
import ArchiveHead from "../../Components/Features/ArchiveHead/ArchiveHead";
import { Image52 } from "../../assets";
import { ethers } from "ethers";
import { NFTList } from "../../Components/Common";
import "./Category.scss";
import { toast } from "react-toastify";

const MAIN_CONTRACTS = [
  { address: "0xF679A38273547875b1730Ae995a1B0d856868843" },
];
const MEDIA_RESOURCES_CONTRACTS = [
  {
    address: "0xeef120f9a8f554b0b78e7b318b9e89f50414c43b",
    name: "Video Archive",
  },
  {
    address: "0x01438b62d0380169bbc8424bad31ed397836a787",
    name: "Palestine Documents Archive",
  },
];

const SUB_CONTRACTS = [
  { address: "0x0869A61f600138a27D649014750d322c6Ed203B3" },
  { address: "0xc1E5857a66CD356455473787a85996fD5ADC21A0" },
  { address: "0xb4916127E78D80C1Ad282A4Ced564F4aa3CDCD61" },
  { address: "0x79851E888ac10Ca4ce5Cf56aC6e5328b8C67c224" },
  { address: "0x910a1c80Eaa70E754175B5D375108a4e789eed8b" },
  { address: "0xEE228A2a1C46c30dcdA079a798cA2F366acE92E5" },
  { address: "0xc807cc9116fb985a12ecd1caf7c1dab3aa2fd73e" },
  { address: "0x05becd2f5562244ddaf503c62941bf77aa2a4826" },
  { address: "0x2638B1390D8Dff934DF58FEa599ca244305E053a" },
  // { address: "0xBDC5Bd332C0827594943D37350B7b7f00585b088" },
];

const PUBLIC_GATEWAY = "https://funooni-public.mypinata.cloud/ipfs";
const ALCHEMY_API_KEY = "cBHgm4PNLXlDi1jcTDugv";

const Category = () => {
  const navigate = useNavigate();

  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const [mainContractNFTs, setMainContractNFTs] = useState([]);
  const [selectedContractNFTs, setSelectedContractNFTs] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionsMetadata, setCollectionsMetadata] = useState({});
  const AES_KEY = process.env.REACT_APP_AES_KEY;

  const allContracts = [...MAIN_CONTRACTS, ...SUB_CONTRACTS];
  const mainContractAddress = MAIN_CONTRACTS[0].address;

  const fetchNFTsForContract = async (contractAddress, setter, userAddress) => {
    setter([]);
    if (!userAddress) return;

    try {
      const url = `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}/getNFTs/?owner=${userAddress}&contractAddresses[]=${contractAddress}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!data.ownedNfts || data.ownedNfts.length === 0) {
        setter([]);
        return 0;
      }

      const fetchedNFTs = await Promise.all(
        data.ownedNfts.map(async (nft) => {
          let tokenUri = nft.tokenUri?.gateway || nft.tokenUri?.raw;
          if (!tokenUri) return null;

          if (tokenUri.startsWith("ipfs://")) {
            tokenUri = `${PUBLIC_GATEWAY}/${tokenUri.replace("ipfs://", "")}`;
          }

          try {
            const metaResponse = await fetch(tokenUri);
            const metadata = await metaResponse.json();
            const imageField = metadata.image || metadata.file;
            const imageUrl = imageField
              ? imageField.startsWith("ipfs://")
                ? `${PUBLIC_GATEWAY}/${imageField.replace("ipfs://", "")}`
                : imageField
              : null;
            return {
              tokenId: nft.tokenId,
              title: metadata.name || `Token #${nft.tokenId}`,
              description: metadata.description || "",
              imageUrl,
              metadata,
            };
          } catch {
            return {
              tokenId: nft.tokenId,
              title: `Token #${nft.tokenId}`,
              description: "",
              imageUrl: null,
              metadata: null,
            };
          }
        }),
      );

      setter(fetchedNFTs.filter(Boolean));
      return fetchedNFTs.filter(Boolean).length;
    } catch (err) {
      console.error(`Error fetching NFTs for ${contractAddress}:`, err);
      return 0;
    }
  };

  useEffect(() => {
    const connectAndVerify = async () => {
      setLoading(true);
      if (!window.ethereum) {
        setLoading(false);
        alert("Please install MetaMask to view this archive.");
        return;
      }
      try {
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });
        if (!accounts || accounts.length === 0) {
          toast.error("Wallet connection refused or canceled.");
        }
        const userAddress = accounts[0];
        setWalletAddress(userAddress);

        const count = await fetchNFTsForContract(
          mainContractAddress,
          setMainContractNFTs,
          userAddress,
        );
        setHasAccess(count > 0);
      } catch (err) {
        console.error("Connection/Verification Error:", err);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };
    connectAndVerify();
  }, [navigate]);

  useEffect(() => {
    const fetchCollectionsMetadata = async () => {
      const provider = new ethers.JsonRpcProvider(
        `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
      );
      const tempMetadata = {};
      await Promise.all(
        allContracts.map(async (c) => {
          try {
            const contract = new ethers.Contract(
              c.address,
              ["function name() view returns (string)"],
              provider,
            );
            const name = await contract.name().catch(() => "Unknown");
            tempMetadata[c.address] = { name, description: "" };
          } catch {
            tempMetadata[c.address] = { name: "Unknown", description: "" };
          }
        }),
      );
      setCollectionsMetadata(tempMetadata);
    };
    fetchCollectionsMetadata();
  }, []);

  useEffect(() => {
    if (!hasAccess || selectedCollection) return;

    const firstSub = SUB_CONTRACTS[0];
    if (firstSub) {
      handleSelectCollection(firstSub);
    }
  }, [collectionsMetadata, hasAccess]);

  useEffect(() => {
    if (!selectedCollection || !walletAddress || !hasAccess) return;

    if (selectedCollection.address !== mainContractAddress) {
      fetchNFTsForContract(
        selectedCollection.address,
        setSelectedContractNFTs,
        walletAddress,
      );
    }
  }, [selectedCollection, walletAddress, hasAccess]);

  const handleSelectCollection = async (collection) => {
    setSelectedCollection({
      ...collection,
      name: collection.name || "Loading...",
      description: "Loading...",
    });

    try {
      const provider = new ethers.JsonRpcProvider(
        `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
      );
      const contract = new ethers.Contract(
        collection.address,
        [
          "function name() view returns (string)",
          "function contractURI() view returns (string)",
        ],
        provider,
      );

      const name = await contract.name().catch(() => "Unknown");
      let imageUrl = null;
      let description = "";
      try {
        let contractURI = await contract.contractURI();
        if (contractURI) {
          contractURI = contractURI.trim();
          const url = contractURI.startsWith("ipfs://")
            ? `${PUBLIC_GATEWAY}/${contractURI.replace("ipfs://", "")}`
            : `${PUBLIC_GATEWAY}/${contractURI}`;
          const res = await fetch(url);
          const json = await res.json();
          description = json.description || "";
          const imgField = json.image || json.file;
          imageUrl = imgField
            ? imgField.startsWith("ipfs://")
              ? `${PUBLIC_GATEWAY}/${imgField.replace("ipfs://", "")}`
              : imgField
            : null;
        }
      } catch {
        description = "";
        imageUrl = null;
      }

      setSelectedCollection({
        ...collection,
        name,
        description,
        image: imageUrl,
      });
    } catch {
      setSelectedCollection({
        ...collection,
        name: "Unknown",
        description: "",
        image: null,
      });
    }
  };

  const mainNFT = mainContractNFTs[0];
  const mainImage = mainNFT?.imageUrl || Image52;
  const mainTitle =
    mainNFT?.title ||
    collectionsMetadata[mainContractAddress]?.name ||
    "My Collection";
  const mainDescription =
    mainNFT?.metadata?.description || mainNFT?.description || "";

  const sidebarCollections = SUB_CONTRACTS.map((c) => ({
    address: c.address,
    name: collectionsMetadata[c.address]?.name || c.address,
    image: null,
    description: collectionsMetadata[c.address]?.description || "",
  }));

  const currentNFTListContract = selectedCollection || MAIN_CONTRACTS[0];

  if (loading) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <h2>Connecting and Verifying Access...</h2>
        <p>Please approve the wallet connection request in MetaMask.</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ padding: "50px", textAlign: "center", color: "red" }}>
        <h2>❌ Access Denied ❌</h2>
        <p>You do not own the required Access NFT for this archive.</p>
        <button
          onClick={() => navigate("/archive-access")}
          style={{ padding: "10px 20px", marginTop: "20px" }}
        >
          Go to Access Gate
        </button>
      </div>
    );
  }

  return (
    <div className="arch-Category-page" style={{ display: "flex" }}>
      <Sidebar
        collections={sidebarCollections}
        Image1={selectedCollection?.image}
        title={selectedCollection?.name}
        description={selectedCollection?.description}
        activeAddress={selectedCollection?.address}
        onSelect={handleSelectCollection}
        MEDIA_RESOURCES_CONTRACTS={MEDIA_RESOURCES_CONTRACTS}
      />

      <div className="arch-Category-pagee">
        {mainNFT && (
          <>
            <ArchiveHead
              image={mainImage}
              title={mainTitle}
              years="1840 - 1950"
            />
            <CollectionDescription
              paragraphs={
                mainDescription
                  ? [mainDescription]
                  : ["No description available for this main collection item."]
              }
            />
          </>
        )}

        {currentNFTListContract ? (
          <NFTList
            contractAddress={currentNFTListContract.address}
            aesKey={AES_KEY}
          />
        ) : (
          <p>Loading collection...</p>
        )}
      </div>
    </div>
  );
};

export default Category;
